import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import { Search, Store, Star, TrendingUp, Package, MapPin, Clock, Phone, Mail, Filter, ChevronDown, SlidersHorizontal, ShoppingBag } from 'lucide-react';

import { ALL_CATEGORIES_WITH_ALL, getCategoryName } from '../utils/categories';

function Marketplace({ user }) {
    const navigate = useNavigate();
    const [sellers, setSellers] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedRegion, setSelectedRegion] = useState('all');
    const [sortBy, setSortBy] = useState('productCount');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [showFilters, setShowFilters] = useState(false);

    // 카테고리 목록 (통합 11개)
    const categories = ALL_CATEGORIES_WITH_ALL.map(cat => ({
        id: cat.id,
        name: cat.id === 'all' ? '전체 카테고리' : cat.name,
    }));

    // 지역 목록
    const regions = [
        { id: 'all', name: '전체 지역' },
        { id: 'seoul', name: '서울' },
        { id: 'gyeonggi', name: '경기' },
        { id: 'incheon', name: '인천' },
        { id: 'busan', name: '부산' },
        { id: 'daegu', name: '대구' },
        { id: 'daejeon', name: '대전' },
        { id: 'gwangju', name: '광주' },
        { id: 'other', name: '기타 지역' },
    ];

    // 정렬 옵션
    const sortOptions = [
        { id: 'productCount', name: '상품 많은순' },
        { id: 'name', name: '이름순' },
        { id: 'rating', name: '평점순' },
        { id: 'latest', name: '최신순' },
    ];

    // 판매업체 로드
    const loadSellers = useCallback(async () => {
        try {
            setLoading(true);
            console.log('🔍 판매업체 로딩 시작...');
            
            const usersRef = collection(db, 'users');
            const q = query(
                usersRef, 
                where('businessType', '==', 'seller'),
                where('sellerStatus', '==', 'approved')
            );

            const snapshot = await getDocs(q);
            
            console.log(`✅ 승인된 판매업체 ${snapshot.size}개 발견`);
            
            if (snapshot.empty) {
                console.warn('⚠️ 승인된 판매업체가 없습니다.');
            }
            
            const sellerList = await Promise.all(
                snapshot.docs.map(async (doc) => {
                    const sellerData = { id: doc.id, ...doc.data() };
                    
                    // 해당 업체의 상품 수 카운트
                    const productsRef = collection(db, 'marketplaceProducts');
                    const productsQuery = query(
                        productsRef,
                        where('sellerId', '==', doc.id),
                        where('status', '==', 'active')
                    );
                    const productsSnapshot = await getDocs(productsQuery);
                    
                    const storeName = sellerData.storeName || sellerData.businessName || sellerData.companyName;
                    console.log(`📦 ${storeName}: ${productsSnapshot.size}개 상품`);
                    
                    return {
                        ...sellerData,
                        productCount: productsSnapshot.size
                    };
                })
            );

            setSellers(sellerList);
            console.log('✅ 판매업체 로딩 완료:', sellerList.length);
        } catch (error) {
            console.error('❌ 판매업체 로딩 실패:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSellers();
    }, [loadSellers]);

    // 스토어 정보 가져오기 헬퍼 함수 (새 필드 우선, 기존 필드 폴백)
    const getStoreName = (seller) => seller.storeName || seller.businessName || seller.companyName || seller.name;
    const getStoreLogo = (seller) => seller.storeLogo || seller.logoUrl || seller.profileImage;
    const getStoreDescription = (seller) => seller.storeDescription || seller.description;
    const getStorePhone = (seller) => seller.storePhone || seller.phone;
    const getStoreEmail = (seller) => seller.storeEmail || seller.email;
    const getStoreAddress = (seller) => seller.storeAddress || seller.address;

    // 지역 매칭 함수
    const matchesRegion = (seller, region) => {
        if (region === 'all') return true;
        const address = getStoreAddress(seller) || '';
        const regionMap = {
            'seoul': '서울',
            'gyeonggi': '경기',
            'incheon': '인천',
            'busan': '부산',
            'daegu': '대구',
            'daejeon': '대전',
            'gwangju': '광주',
        };
        if (region === 'other') {
            return !Object.values(regionMap).some(r => address.includes(r));
        }
        return address.includes(regionMap[region] || '');
    };

    // 검색 및 필터링 (스토어 공개 여부 포함)
    const filteredSellers = sellers.filter(seller => {
        // 비공개 스토어 제외
        if (seller.storeVisible === false) return false;

        const storeName = getStoreName(seller);
        const storeDesc = getStoreDescription(seller);

        const matchesSearch = !searchQuery ||
            storeName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            storeDesc?.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesCategory = selectedCategory === 'all' ||
            seller.category === selectedCategory;

        const matchesSelectedRegion = matchesRegion(seller, selectedRegion);

        return matchesSearch && matchesCategory && matchesSelectedRegion;
    }).sort((a, b) => {
        switch (sortBy) {
            case 'productCount':
                return (b.productCount || 0) - (a.productCount || 0);
            case 'name':
                return (getStoreName(a) || '').localeCompare(getStoreName(b) || '');
            case 'rating':
                return (b.rating || 0) - (a.rating || 0);
            case 'latest':
                return (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0);
            default:
                return 0;
        }
    });

    const goToSellerStore = (sellerId) => {
        navigate(`/marketplace/seller/${sellerId}`);
    };

    if (loading) {
        return (
            <div style={styles.loading}>
                <div style={styles.spinner}></div>
                <p>업체 목록을 불러오는 중...</p>
            </div>
        );
    }

    // 필터 초기화
    const resetFilters = () => {
        setSelectedCategory('all');
        setSelectedRegion('all');
        setSortBy('productCount');
        setSearchQuery('');
    };

    // 활성 필터 수
    const activeFilterCount = [
        selectedCategory !== 'all',
        selectedRegion !== 'all',
    ].filter(Boolean).length;

    return (
        <div style={styles.container}>
            {/* 검색 영역 */}
            <div style={styles.searchSection}>
                <div style={styles.searchContainer}>
                    <div style={styles.searchBox}>
                        <Search size={20} color="#6b7280" />
                        <input
                            type="text"
                            placeholder="업체명, 제품, 브랜드 검색..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={styles.searchInput}
                        />
                    </div>
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        style={{
                            ...styles.filterToggleBtn,
                            ...(showFilters || activeFilterCount > 0 ? styles.filterToggleBtnActive : {})
                        }}
                    >
                        <SlidersHorizontal size={18} />
                        필터
                        {activeFilterCount > 0 && (
                            <span style={styles.filterBadge}>{activeFilterCount}</span>
                        )}
                    </button>
                </div>

                {/* 필터 패널 */}
                {showFilters && (
                    <div style={styles.filterPanel}>
                        <div style={styles.filterRow}>
                            <div style={styles.filterGroup}>
                                <label style={styles.filterLabel}>지역</label>
                                <select
                                    value={selectedRegion}
                                    onChange={(e) => setSelectedRegion(e.target.value)}
                                    style={styles.filterSelect}
                                >
                                    {regions.map(region => (
                                        <option key={region.id} value={region.id}>{region.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div style={styles.filterGroup}>
                                <label style={styles.filterLabel}>정렬</label>
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    style={styles.filterSelect}
                                >
                                    {sortOptions.map(option => (
                                        <option key={option.id} value={option.id}>{option.name}</option>
                                    ))}
                                </select>
                            </div>
                            {activeFilterCount > 0 && (
                                <button onClick={resetFilters} style={styles.resetBtn}>
                                    필터 초기화
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* 카테고리 탭 */}
            <div style={styles.categoryTabs}>
                {categories.map(cat => (
                    <button
                        key={cat.id}
                        onClick={() => setSelectedCategory(cat.id)}
                        style={{
                            ...styles.categoryTab,
                            ...(selectedCategory === cat.id ? styles.categoryTabActive : {})
                        }}
                    >
                        {cat.name}
                    </button>
                ))}
            </div>

            {/* 결과 정보 */}
            <div style={styles.resultInfo}>
                <span style={styles.resultCount}>
                    총 <strong>{filteredSellers.length}</strong>개 업체
                </span>
                <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    style={styles.sortSelect}
                >
                    {sortOptions.map(option => (
                        <option key={option.id} value={option.id}>{option.name}</option>
                    ))}
                </select>
            </div>

            {/* 업체 목록 */}
            <div style={styles.sellersGrid}>
                {filteredSellers.length === 0 ? (
                    <div style={styles.emptyState}>
                        <Store size={64} color="#d1d5db" />
                        <p style={styles.emptyStateText}>
                            {sellers.length === 0 
                                ? '등록된 판매업체가 없습니다' 
                                : '검색 결과가 없습니다'}
                        </p>
                        <p style={styles.emptyStateSubtext}>
                            {sellers.length === 0 
                                ? '판매자가 먼저 업체 등록 및 승인을 받아야 합니다' 
                                : '다른 카테고리를 확인해보세요'}
                        </p>
                    </div>
                ) : (
                    filteredSellers.map(seller => (
                        <div 
                            key={seller.id} 
                            style={styles.sellerCard}
                            onClick={() => goToSellerStore(seller.id)}
                        >
                            {/* 인기 업체 뱃지 */}
                            {seller.isPopular && (
                                <div style={styles.popularBadge}>
                                    <TrendingUp size={12} />
                                    인기
                                </div>
                            )}

                            {/* 업체 로고 */}
                            <div style={styles.sellerImageContainer}>
                                {getStoreLogo(seller) ? (
                                    <img
                                        src={getStoreLogo(seller)}
                                        alt={getStoreName(seller)}
                                        style={styles.sellerImage}
                                    />
                                ) : (
                                    <div style={styles.noImage}>
                                        <Store size={36} color="#d1d5db" />
                                    </div>
                                )}
                            </div>

                            {/* 업체 정보 */}
                            <div style={styles.sellerInfo}>
                                <h3 style={styles.sellerName}>{getStoreName(seller)}</h3>
                                <div style={styles.contactSection}>
                                    {getStorePhone(seller) && (
                                        <div style={styles.contactInfo}>
                                            <Phone size={13} color="#6b7280" />
                                            <span>{getStorePhone(seller)}</span>
                                        </div>
                                    )}
                                    {getStoreEmail(seller) && (
                                        <div style={styles.contactInfo}>
                                            <Mail size={13} color="#6b7280" />
                                            <span>{getStoreEmail(seller)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        padding: '24px',
    },
    header: {
        marginBottom: '24px',
    },
    title: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#1e293b',
        margin: '0 0 8px 0',
    },
    subtitle: {
        fontSize: '15px',
        color: '#64748b',
        margin: 0,
    },
    searchSection: {
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '16px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        border: '1px solid #e2e8f0',
    },
    searchContainer: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
    },
    searchBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '14px 18px',
        backgroundColor: '#f8fafc',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
        flex: 1,
    },
    searchInput: {
        flex: 1,
        border: 'none',
        backgroundColor: 'transparent',
        fontSize: '15px',
        outline: 'none',
        color: '#1e293b',
    },
    filterToggleBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '14px 20px',
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#64748b',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    filterToggleBtnActive: {
        backgroundColor: '#6366f1',
        borderColor: '#6366f1',
        color: 'white',
    },
    filterBadge: {
        backgroundColor: 'white',
        color: '#6366f1',
        padding: '2px 8px',
        borderRadius: '10px',
        fontSize: '12px',
        fontWeight: '600',
    },
    filterPanel: {
        marginTop: '16px',
        paddingTop: '16px',
        borderTop: '1px solid #e2e8f0',
    },
    filterRow: {
        display: 'flex',
        gap: '16px',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
    },
    filterGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        minWidth: '160px',
    },
    filterLabel: {
        fontSize: '12px',
        fontWeight: '600',
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    filterSelect: {
        padding: '10px 14px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        backgroundColor: '#f8fafc',
        fontSize: '14px',
        color: '#1e293b',
        cursor: 'pointer',
        outline: 'none',
    },
    resetBtn: {
        padding: '10px 16px',
        backgroundColor: 'transparent',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '13px',
        color: '#64748b',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    resultInfo: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        padding: '0 4px',
    },
    resultCount: {
        fontSize: '14px',
        color: '#64748b',
    },
    sortSelect: {
        padding: '8px 12px',
        borderRadius: '6px',
        border: '1px solid #e2e8f0',
        backgroundColor: 'white',
        fontSize: '13px',
        color: '#475569',
        cursor: 'pointer',
        outline: 'none',
    },
    sellersGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        gap: '14px',
        marginBottom: '40px',
    },
    sellerCard: {
        backgroundColor: 'white',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        border: '1px solid #e2e8f0',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
    },
    bannerContainer: {
        width: '100%',
        height: '80px',
        overflow: 'hidden',
    },
    bannerImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    sellerImageContainer: {
        position: 'relative',
        width: '100%',
        paddingBottom: '100%',
        backgroundColor: '#f8f9fa',
        overflow: 'hidden',
    },
    sellerImage: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        padding: '12px',
    },
    noImage: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    sellerInfo: {
        padding: '10px 12px',
        position: 'relative',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
    },
    logoNameRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '6px',
    },
    smallLogo: {
        width: '36px',
        height: '36px',
        borderRadius: '6px',
        objectFit: 'cover',
        border: '1px solid #e5e7eb',
    },
    sellerName: {
        fontSize: '15px',
        fontWeight: '700',
        color: '#111827',
        marginBottom: '4px',
        margin: 0,
    },
    businessNumber: {
        fontSize: '11px',
        color: '#6b7280',
        marginBottom: '8px',
        marginTop: '4px',
        fontFamily: 'monospace',
    },
    sellerDescription: {
        fontSize: '13px',
        color: '#6b7280',
        marginBottom: '8px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        lineHeight: '1.4',
    },
    sellerDescriptionEmpty: {
        fontSize: '13px',
        color: '#d1d5db',
        marginBottom: '8px',
        fontStyle: 'italic',
    },
    divider: {
        height: '1px',
        backgroundColor: '#e5e7eb',
        margin: '8px 0',
    },
    contactSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        marginBottom: '8px',
    },
    contactInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        fontSize: '12px',
        color: '#6b7280',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    ratingContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '3px',
    },
    rating: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#111827',
    },
    reviewCount: {
        fontSize: '11px',
        color: '#6b7280',
    },
    sellerMetaContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        marginBottom: '4px',
    },
    sellerMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '12px',
        color: '#6b7280',
    },
    businessHours: {
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        fontSize: '11px',
        color: '#6b7280',
        marginTop: '6px',
        paddingTop: '6px',
        borderTop: '1px solid #f3f4f6',
    },
    popularBadge: {
        position: 'absolute',
        top: '8px',
        right: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '3px',
        padding: '4px 10px',
        backgroundColor: '#ef4444',
        color: 'white',
        borderRadius: '20px',
        fontSize: '11px',
        fontWeight: '600',
        zIndex: 10,
    },
    emptyState: {
        gridColumn: '1 / -1',
        textAlign: 'center',
        padding: '80px 20px',
    },
    emptyStateText: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#6b7280',
        marginTop: '16px',
    },
    emptyStateSubtext: {
        fontSize: '14px',
        color: '#9ca3af',
        marginTop: '8px',
    },
    loading: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        fontSize: '16px',
        color: '#6b7280',
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '4px solid #e5e7eb',
        borderTop: '4px solid #0066cc',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
    },
    categoryTabs: {
        display: 'flex',
        gap: '8px',
        overflowX: 'auto',
        padding: '0 0 16px 0',
        marginBottom: '16px',
    },
    categoryTab: {
        padding: '8px 16px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#64748b',
        backgroundColor: '#f8fafc',
        border: '2px solid transparent',
        borderRadius: '20px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'all 0.2s',
    },
    categoryTabActive: {
        color: '#6366f1',
        backgroundColor: '#eef2ff',
        borderColor: '#6366f1',
    },
    badgeRow: {
        display: 'flex',
        gap: '6px',
        marginTop: '4px',
        marginBottom: '4px',
    },
    partnerBadge: {
        padding: '2px 8px',
        fontSize: '11px',
        fontWeight: '600',
        color: '#0369a1',
        backgroundColor: '#e0f2fe',
        borderRadius: '10px',
    },
    recommendedBadge: {
        padding: '2px 8px',
        fontSize: '11px',
        fontWeight: '600',
        color: '#15803d',
        backgroundColor: '#dcfce7',
        borderRadius: '10px',
    },
};

// 스피너 애니메이션
const styleSheet = document.createElement("style");
styleSheet.textContent = `
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
    
    @media (hover: hover) {
        [style*="sellerCard"]:hover {
            transform: translateY(-4px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        }
    }
`;
document.head.appendChild(styleSheet);

export default Marketplace;